<template>
  <v-skeleton-loader
    v-if="skeleton"
    :loading="skeleton"
    type="table"
  />

  <v-data-table
    v-else
    ref="table"
    :key="keySortablePositions"
    v-bind="$attrs"
    :value="selected"
    :headers="tableHeaders"
    :items="items"
    :item-class="getItemClass"
    :expanded.sync="expanded"

    :server-items-length="pagination.count"
    :show-expand="showExpand"

    :item-key="itemKey"
    :loading="loading"

    :show-select="showSelect"
    :single-expand="singleExpand"

    :options="options"
    :footer-props="footerProps"
    :hide-default-footer="hideDefaultFooter"

    :sort-by.sync="sortBy"
    :sort-desc.sync="sortDesc"
    :multi-sort="multiSort"

    class="b-table"
    v-on="listeners"
    @pagination="onChangePagination"
    @update:sort-desc="onSort"
  >
    <!--  -->

    <template #item.sortable-positions>
      <v-icon
        class="js-sortable-positions-handle"
        :class="{ 'js-sortable-positions-handle--disabled': items.length < 2 }"
      >
        mdi-drag
      </v-icon>
    </template>

    <template #item.data-table-expand="props">
      <slot
        name="item.item.data-table-expand"
        v-bind="props"
        :data="getData(props.item)"
      >
        <BTableButtonExpand
          :active="props.isExpanded"
          :disabled="!checkShowExpand(getData(props.item), props)"
          @click="onExpand(props, getData(props.item))"
        />
      </slot>
    </template>

    <template #item.actions="{ header, item, index }">
      <div class="text-no-wrap">
        <template v-for="action in header.actions">
          <slot
            :name="`item.actions.${action.action}`"
            v-bind="{ action, header, item, index, data: getData(item) }"
          >
            <BTableButtonAction
              v-if="(
                !item.actions
                || action.action === viewAction
                || action.action === copyAction
                || item.actions.includes(action.action)
              )"
              :key="action.action"
              :action="action"
              :index="index"
              :data="getData(item)"
              :list="list"
              v-on="$listeners"
              @change:positions="onChangePositions"
            />
          </slot>
        </template>
      </div>
    </template>

    <template v-for="(_, key) in $scopedSlots" #[key]="props">
      <slot v-bind="props" :data="getData(props.item)" :name="key" />
    </template>

    <!--  -->
  </v-data-table>
</template>

<script>
import memoize from 'lodash/memoize'
import trim from 'lodash/trim'
import isEqual from 'lodash/isEqual'
import get from 'lodash/get'
import { DEFAULT_PER_PAGE_OPTIONS } from '@/helpers/enums/params'
import {
  TableActionsTypes,
  SORTABLE_POSITIONS_UP,
  SORTABLE_POSITIONS_DOWN,
} from '@/helpers/enums/table-actions'


export const BTableButtonAction = () => import(
  /* webpackChunkName: "BTableButtonAction" */
  './BTable/BTableButtonAction.vue'
)

export const BTableButtonExpand = () => import(
  /* webpackChunkName: "BTableButtonExpand" */
  './BTable/BTableButtonExpand.vue'
)

export const createActionHeader = (actions, fullWidthActions) => ({
  value: 'actions',
  sortable: false,
  actions,
  width: fullWidthActions ? `${actions.length * 28 + 2 * 8 + 2}px` : '',
  cellClass: 'text-end',
})

const HEADER_TABLE_EXPAND = {
  text: '',
  value: 'data-table-expand',
  sortable: false,
  width: 32,
}

const HEADER_SORTABLE_POSITIONS = {
  value: 'sortable-positions',
  sortable: false,
  width: 40,
}

export default {
  name: 'BTable',
  components: {
    BTableButtonAction,
    BTableButtonExpand,
  },
  inheritAttrs: false,
  model: {
    prop: 'selected',
    event: 'input',
  },
  props: {
    showExpand: Boolean,
    singleExpand: Boolean,
    checkShowExpand: {
      type: Function,
      default: () => true,
    },
    skeleton: Boolean,
    sortablePositions: Boolean,
    fullWidthActions: Boolean,
    list: {
      type: Array,
      default: () => [],
    },
    headers: {
      type: Array,
      required: true,
    },
    actions: {
      type: Array,
    },
    pagination: {
      type: Object,
      default: () => ({
        page: void 0,
        per_page: void 0,
        count: void 0,
      }),
      validator: (prop) => (
        ('page' in prop)
        && ('per_page' in prop)
        && ('count' in prop)
      ),
    },
    itemClass: {
      type: [String, Function],
      default: '',
    },
    itemKey: {
      type: String,
      default: 'id',
    },
    itemsPerPageOptions: {
      type: Array,
      default: () => DEFAULT_PER_PAGE_OPTIONS,
    },
    ordering: String,
    selected: Array,
    loading: Boolean,
  },
  data() {
    return {
      // не хочет таблица перерисовываться когда приходит новый список, придется помочь
      keySortablePositions: '',
      listeners: this.$listeners || Object.freeze({}),
      sortBy: void 0,
      sortDesc: void 0,
      expanded: [],
      viewAction: TableActionsTypes.view,
      copyAction: TableActionsTypes.copy,
    }
  },
  computed: {
    items() {
      const { headers, list } = this

      const hasCustomField = headers.find((_) => typeof _.field === 'function')
      if (!hasCustomField) return this.list

      return list.map((data, index) => {
        const result = headers.reduce((acc, header) => {
          const value = get(data, header.value)

          if (typeof header.field === 'function') {
            const res = header.field.call(this, value, data, header, index)
            acc[header.value] = res
          } else {
            acc[header.value] = value
          }

          return acc
        }, {})

        return { ...data, ...result }
      })
    },
    tableHeaders() {
      // eslint-disable-next-line object-curly-newline
      const { actions, sortablePositions, items, showExpand } = this
      let headers = this.headers.slice()

      if (items.length && (actions?.length || sortablePositions || showExpand)) {
        let allActions = [].concat(actions || [])

        if (showExpand) {
          headers.unshift(HEADER_TABLE_EXPAND)
        }

        if (sortablePositions) {
          headers.unshift(HEADER_SORTABLE_POSITIONS)

          allActions = [].concat(
            SORTABLE_POSITIONS_UP,
            SORTABLE_POSITIONS_DOWN,
            allActions,
          )
        }

        if (actions?.length) {
          const headerAction = createActionHeader(allActions, this.fullWidthActions)
          headers.push(headerAction)
        }
      }

      if (!items.length) {
        // если нет данных, то и сортировку отключим
        headers = headers.map((_) => ({ ..._, sortable: false }))
      }

      return headers
    },
    multiSort() {
      return this.headers.filter((_) => _.sortable).length > 1
    },
    showSelect() {
      return Array.isArray(this.selected)
    },
    footerProps() {
      const { list, pagination, itemsPerPageOptions } = this
      const count = pagination.count || 0

      return {
        disableItemsPerPage: (
          list.length <= itemsPerPageOptions[0]
          && count <= itemsPerPageOptions[0]
        ),
        itemsPerPageOptions,
        page: pagination.page || 1,
        itemsPerPage: pagination.per_page || itemsPerPageOptions[0],
        itemsPerPageText: '',
      }
    },
    hideDefaultFooter() {
      const { pagination, list } = this
      const [perPage] = this.itemsPerPageOptions
      return (
        (pagination.count || list.length) <= perPage
        || perPage === -1
      )
    },
    options() {
      const { page, itemsPerPage } = this.footerProps
      return {
        page,
        itemsPerPage,
      }
    },
  },
  watch: {
    ordering: {
      immediate: true,
      handler(value) {
        const { multiSort } = this
        const ordering = value?.split(',') || []

        let sortBy = ordering.map((data) => trim(data, '-'))
        sortBy = multiSort ? sortBy : sortBy[0]
        if (!isEqual(sortBy, this.sortBy)) this.sortBy = sortBy

        let sortDesc = ordering.map((data) => data[0] !== '-')
        sortDesc = multiSort ? sortDesc : sortDesc[0]
        if (!isEqual(sortDesc, this.sortDesc)) this.sortDesc = sortDesc
      },
    },
    list: 'initSortablePositions',
    sortablePositions: 'initSortablePositions',
    skeleton: 'initSortablePositions',
  },
  created() {
    this.getData = memoize(this.getData)
  },
  mounted() {
    this.initSortablePositions()
  },
  beforeDestroy() {
    void this.sortableElm?.destroy()
  },
  methods: {
    async initSortablePositions() {
      void this.sortableElm?.destroy()
      this.sortableElm = null
      if (!this.sortablePositions) return
      this.keySortablePositions = Date.now()

      const { Sortable } = await import(
        /* webpackChunkName: "sortablejs" */
        'sortablejs'
      )

      const element = this.$refs.table?.$el.querySelector('tbody')
      if (!element) return

      this.sortableElm = Sortable.create(element, {
        animation: 150,
        handle: '.js-sortable-positions-handle',
        draggable: 'tr',
        ghostClass: 'is-ghost',
        dragClass: 'is-drag',
        onStart: (e) => {
          e.item.classList.add('is-chosen')
        },
        onEnd: (e) => {
          e.item.classList.remove('is-chosen')
        },
        onSort: (e) => {
          this.onChangePositions(e.oldIndex, e.newIndex)
        },
      })

      const { expanded } = this
      this.expanded = []
      await this.$nextTick()

      this.expanded = expanded.map(({ [this.itemKey]: id }) => (
        this.list.find((_) => _[this.itemKey] === id)
      ))
    },
    onChangePagination(data) {
      const { pagination } = this

      // При старте этот евент не нужно обрабатывать
      if (!this.list.length || !pagination.page) return

      const params = {
        ...pagination,
        page: data.page,
        per_page: data.itemsPerPage,
      }

      // Повторные запросы глушим
      if (isEqual(params, pagination)) return
      this.$emit('change:pagination', params)
    },
    onSort() {
      const sortBy = [].concat(this.sortBy).filter(Boolean)
      const sortDesc = [].concat(this.sortDesc).filter(Boolean)

      const ordering = sortBy.map((sort, index) => {
        const direction = sortDesc[index] ? '' : '-'
        return `${direction}${sort}`
      }).join(',') || void 0

      if (!isEqual(ordering, this.ordering)) {
        this.$emit('change:sort', ordering)
      }
    },
    onChangePositions(oldIndex, newIndex, callback) {
      const list = this.list.slice()
      const [data] = list.splice(oldIndex, 1)
      list.splice(newIndex, 0, data)
      this.$emit('change:sortable-positions', list, callback)
    },
    getItemClass(item) {
      if (this.itemClass) {
        return typeof this.itemClass === 'function' ? this.itemClass(item) : this.itemClass
      }
      if (this.sortablePositions) return 'js-sortable-positions'
      return ''
    },
    onExpand(props, data) {
      const { expanded } = this

      if (props.isExpanded) {
        const index = expanded.indexOf(props.item)
        if (index !== -1) expanded.splice(index, 1)
      } else if (this.singleExpand) {
        this.expanded = [props.item]
      } else {
        expanded.push(props.item)
      }

      this.$emit('toggle:expanded', !props.isExpanded, data)
    },
    getData(item) {
      const index = this.items.indexOf(item)
      return this.list[index]
    },
  },
}
</script>

<style lang="scss">

.b-table {
  &__sortable-positions {
    padding-right: 0;
    padding-left: 0;
  }

  th,
  td {
    height: 24px !important;
    padding: 5px 8px !important;
  }

  td {
    word-break: break-word;
  }
}

tr.v-data-table__progress th {
  height: auto !important;
  padding: 0 !important;
}

.js-sortable-positions {
  $root: &;

  &-handle {
    cursor: grab;

    &--disabled {
      pointer-events: none;
      opacity: 0.5;
    }
  }
}
</style>
